import { create } from "zustand";
import { z } from "zod";
import moment from "moment-timezone";
import { useState, useMemo } from "react";
import parsePhoneNumber, {
  AsYouType as PhoneNumberFormatterAsYouType,
} from "libphonenumber-js";
import {
  BaseCustomer,
  CreateCustomerValidation,
  useCreateCustomer,
  useListCustomers,
  useUpdateCustomer,
  customerTypeOptions,
  useListClientBrokerAccountNumbers,
  useUpdatePartialCustomer,
  PartialUpdateCustomerValidation,
  useListOnboardingCustomerInstances,
  AvailableOnboardingInstanceInfo,
} from "../../lib/api/customer";
import { DeferredPaymentTypeOptions } from "@/lib/api/deferred_payments";
import { downloadCsvTextAsFile } from "../../lib/browser/download";
import { Skeleton } from "../../components/ui/skeleton";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
  DialogFooter,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useToast } from "../../components/ui/use-toast";
import { Checkbox } from "../../components/ui/checkbox";
import { DataTable, dateWithFormatSort, type DataTableColumn } from "@/components/ui/DataTable/index";
import { findFilteredDifferences, isValidEmail } from "../../lib/prelude";
import { findCustomerFrom } from "../../lib/customer";
import { Customer } from "@/lib/api/reports";
import { CommonDestinationTimezone, GenerateLoadingOrError } from "@/lib/utils";
import { Combobox } from "../../components/ui/combobox";
import useDebounce from "../otc/useDebounce";
import { Check as CheckIcon, X as XIcon } from "lucide-react";
import { useListBaseTraders, useListTraders } from "@/lib/api/trader";
import { formatNumberToDisplay } from "@/lib/api/common";

interface SourcePhoneNumber {
  source_type: "destination" | "mobile_user";
  label: string;
  phone_number: string;
}

interface SourceEmail {
  source_type: "payment" | "pdf_signature" | "payment_ach" | "payment_check";
  label: string;
  email: string;
}

interface UpdateCustomerInfo {
  client_id?: string;
  short_name?: string;
  long_name?: string;

  harvest_iq_profile?: string;
  skip_harvest_iq_sync?: boolean;

  is_commercial?: boolean;
  is_cfr?: boolean;

  address_line_one?: string;
  address_line_two?: string;
  city?: string;
  zipcode?: string
  state?: string;
  country?: string;
  email?: string;
  phone_number?: string;
  notes?: string;

  gps_latitude?: string;
  gps_longitude?: string;

  preferred_defer_payment_type?: string;

  source_phone_numbers?: SourcePhoneNumber[];
  source_emails?: SourceEmail[];
  customer_type?: string;
  active_account_codes?: string[];

  trader_id?: string;
  onboarding_instance_id?: string;
  signed_up_date?: string;
  
  // Hidden fields from onboarding instance
  partner_names?: string[];
  shirt_sizes?: string[];
  original_acres?: number;
  bol_sequence?: string;
}

interface BearState {
  exception_id?: string;
  exception_type?: string;
  updateCustomerId?: string;
  updateCustomer: UpdateCustomerInfo;
  originalUpdateCustomer: UpdateCustomerInfo;

  setCustomerId: (id: string) => void;
  setCustomer: (customer: BaseCustomer) => void;
  updateCustomerField: (key: string, value: any) => void;
  clearCustomer: () => void;
}

export const useBearStore = create<BearState>((set) => ({
  updateCustomerId: undefined,
  originalUpdateCustomer: {
    is_commercial: false,
    exception_id: undefined,
    exception_type: undefined,
  },
  updateCustomer: {
    is_commercial: false,
    exception_id: undefined,
    exception_type: undefined,
    signed_up_date: moment().format("YYYY-MM-DD"),
  },

  setCustomerId: (customerId: string) =>
    set((state) => ({ updateCustomerId: customerId })),
  clearCustomer: () =>
    set((state) => ({
      updateCustomer: { 
        is_commercial: false,
        signed_up_date: moment().format("YYYY-MM-DD"),
      },
      updateCustomerId: undefined,
    })),
  setCustomer: (customer: BaseCustomer) =>
    set((state) => ({
      updateCustomerId: customer.id,
      updateCustomer: customer as any,
      originalUpdateCustomer: customer as any,
    })),
  updateCustomerField: (key: string, value: string) =>
    set((state) => ({
      ...state,
      updateCustomer: {
        ...state.updateCustomer,
        [key]: value,
      },
    })),
}));

export const ManageCustomersPage = () => {
  const customersResult = useListCustomers();
  const [isModalOpen, setModalOpenStatus] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);
  const clearCustomer = useBearStore((state) => state.clearCustomer);
  const setCustomer = useBearStore((state) => state.setCustomer);

  if ([customersResult].some((result) => result.isLoading)) {
    return (
      <div className="flex flex-col space-y-2">
        <Skeleton className="h-4 w-[250px]" />
        <Skeleton className="h-4 w-[200px]" />
      </div>
    );
  }

  const onUpdateCreateModalStatus = (status: boolean) => {
    setModalOpenStatus(status);

    if (!status) {
      clearCustomer();
    }
  };

  const onSetupUpdateCustomer = (customer: BaseCustomer) => {
    setCustomer(customer);
    setModalOpenStatus(true);
  };

  const customerList = customersResult?.data?.data || [];

  const filteredCustomers = debouncedSearch
    ? customerList?.filter((customer: Customer) => {
      const matchedCustomer = findCustomerFrom("", debouncedSearch, [
        customer,
      ]);
      return !!matchedCustomer;
    }) || []
    : customerList;

  // Define columns for DataTable
  const columns: DataTableColumn<BaseCustomer & { created_at: string, updated_at: string }>[] = [
    {
      id: "client_id",
      accessorKey: "client_id",
      header: "Id",
      size: 120,
    },
    {
      id: "short_name",
      accessorKey: "short_name",
      header: "Short Name",
      size: 150,
    },
    {
      id: "long_name",
      accessorKey: "long_name",
      header: "Long Name",
      minSize: 300,
      enableTooltip: true,
    },
    {
      id: "is_commercial",
      accessorKey: "is_commercial",
      header: "Commercial",
      size: 100,
      cell: ({ row }: any) => row.original.is_commercial ? <CheckIcon className="h-4 w-4" /> : <XIcon className="h-4 w-4" />,
    },
    {
      id: "email",
      accessorKey: "email",
      header: "Email",
      size: 200,
      enableTooltip: true,
    },
    {
      id: "phone_number",
      accessorKey: "phone_number",
      header: "Phone Number",
      size: 150,
      cell: ({ getValue }: any) => {
        const phone_number = getValue();
        if (!phone_number) return "";
        return parsePhoneNumber(phone_number, 'US')?.format('NATIONAL') || phone_number;
      },
    },
    {
      id: "city",
      accessorKey: "city",
      header: "City",
      size: 120,
    },
    {
      id: "state",
      accessorKey: "state",
      header: "State",
      size: 80,
    },
    {
      id: "country",
      accessorKey: "country",
      header: "Country",
      size: 100,
    },
    {
      id: "customer_type",
      accessorKey: "customer_type",
      header: "Type",
      size: 120,
      accessorFn: (row: any) => {
        const type = customerTypeOptions?.find(item => item.value === row.customer_type);
        return type?.label || row.customer_type || "";
      },
      cell: ({ getValue }: any) => getValue(),
    },
    {
      id: "is_syncing_to_risk",
      accessorKey: "is_syncing_to_risk",
      accessorFn: (row: any) => row.is_syncing_to_risk ? "Yes" : "No",
      header: "Syncs to Risk",
      size: 120,
      cell: ({ getValue }: any) => getValue(),
    },
    {
      id: "Trader",
      accessorKey: "Trader",
      accessorFn: (row: any) => row.Trader?.name || "",
      header: "Trader",
      size: 120,
      cell: ({ getValue }: any) => getValue(),
    },
    {
      id: "notes",
      accessorKey: "notes",
      header: "Notes",
      size: 220,
    },
    {
      id: "original_acres",
      accessorKey: "original_acres",
      header: "Original Acres",
      size: 150,
      cell: ({ getValue }: any) => formatNumberToDisplay(getValue()),
    },
    {
      id: "bol_sequence",
      accessorKey: "bol_sequence",
      header: "BOL Sequence",
      size: 160,
    },
    {
      id: "partner_names",
      accessorKey: "partner_names",
      header: "Partners",
      size: 220,
      cell: ({ getValue }: any) => getValue()?.join(", ") || "",
    },
    {
      id: "signed_up_date",
      accessorKey: "signed_up_date",
      header: "Signup At",
      cell: ({ row }: any) => row.original.signed_up_date ? moment(row.original.signed_up_date, "YYYY-MM-DD").format("MM/DD/YYYY") : "",
      getGroupingValue: (row) => moment(row.signed_up_date, "YYYY-MM-DD").format("MM/DD/YYYY"),
      size: 160,
      enableColumnFilter: false,
    },
    {
      id: "created_at",
      accessorKey: "created_at",
      header: "Created At",
      cell: ({ row }: any) => moment.tz(row.original.created_at, CommonDestinationTimezone).format("MM/DD/YYYY"),
      getGroupingValue: (row) => moment.tz(row.created_at, CommonDestinationTimezone).format("MM/DD/YYYY"),
      size: 160,
      enableColumnFilter: false,
    },
    {
      id: "updated_at",
      accessorKey: "updated_at",
      header: "Updated At",
      cell: ({ row }: any) => moment.tz(row.original.updated_at, CommonDestinationTimezone).format("MM/DD/YYYY"),
      getGroupingValue: (row) => moment.tz(row.updated_at, CommonDestinationTimezone).format("MM/DD/YYYY"),
      size: 160,
      enableColumnFilter: false,
    },
  ];

  return (
    <section className="flex min-h-full flex-1 flex-col px-6 py-6 lg:px-8">
      <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
        Manage Customers
      </h1>
      <div className="flex flex-col-reverse gap-4 sm:flex-row sm:justify-between sm:items-center my-4">
        <Input
          placeholder="Search customers..."
          className="max-w-xs"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className="flex flex-col sm:flex-row gap-2">
          <DownloadCustomerButton customerList={customerList} />
          <CustomerModal
            isModalOpen={isModalOpen}
            onUpdateCreateModalStatus={onUpdateCreateModalStatus}
          />
        </div>
      </div>
      <div className="flex-1 min-h-0">
        <DataTable
          enableColumnPinning
          persistColumnSettings
          tableId="ManageCustomersTables"
          data={filteredCustomers}
          columns={columns as any[]}
          className="shadow-lg"
          tableClassName="max-h-[calc(100vh-340px)] overflow-y-auto"
          stickyHeaders
          enableColumnVisibility
          enableSorting
          enableMultiSort
          enableFiltering
          enableGrouping
          enableColumnResizing
          virtualizationOverscan={20}
          virtualizationRowHeight={44}
          enableVirtualization
          enableExport
          filterStyle="select"
          filterMode="internal"
          emptyMessage={customersResult.isLoading ? "Loading..." : "No customers found"}
          defaultSorting={[{ id: "long_name", desc: false }]}
          rowClickable
          onRowClick={(item: any) => {
            onSetupUpdateCustomer(item);
          }}
        />
      </div>
    </section>
  );
};

async function writeCsv(customerList: BaseCustomer[], filename: string) {
  const headers = [
    "Id",
    "Short Name",
    "Long Name",
    "State",
    "Country",
    "Email",
    "Is Commercial",
  ];

  return {
    success: true,
    data: [
      headers.join(","),
      ...customerList.map((customer) => {
        return [
          customer.client_id,
          customer.short_name,
          customer.long_name,
          customer.state,
          customer.country,
          customer.email,
          `${customer.is_commercial}`,
        ]
          .map((item) => item || "")
          .map((item) => (item.includes(",") ? `"${item}"` : item))
          .join(",");
      }),
    ].join("\n"),
  };
}

function DownloadCustomerButton({
  customerList,
}: {
  customerList: BaseCustomer[];
}) {
  const { toast } = useToast();
  const onDownload = async (filename: string) => {
    const result = await writeCsv(customerList, filename);

    if (!result.success) {
      toast({
        title: "Failed to download customer list",
        description: "",
      });
    } else {
      toast({
        title: "Downloaded",
        description: "",
      });
      downloadCsvTextAsFile(filename, result.data);
    }
  };

  const fetchDate = moment().format("MM_DD_YY");

  return (
    <Button
      type="button"
      variant="outline"
      className="mr-4"
      disabled={false}
      onClick={() => onDownload(`customer_list_${fetchDate}.csv`)}
    >
      Download Customer List
    </Button>
  );
}

export function CustomerModal({
  isModalOpen,
  onUpdateCreateModalStatus,
}: {
  isModalOpen: boolean;
  onUpdateCreateModalStatus: (item: boolean) => void;
}) {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const updateCustomerId = useBearStore((state) => state.updateCustomerId);
  const originalUpdateCustomer = useBearStore((state) => state.originalUpdateCustomer);
  const createCustomer = useCreateCustomer();
  const updateCustomer = useUpdatePartialCustomer();

  const isUpdateForm = !!updateCustomerId;

  const deriveUpdateCustomerFields = (updates: any, original: any) => {

    const newBaseComparable = transformToCustomerComparablePosition(updates);
    const existingComparableData = transformToCustomerComparablePosition(original);

    const updatesCheck = findFilteredDifferences(newBaseComparable, existingComparableData, {
      removed: true,
      changed: true,
      new: true,
    });

    if (!updatesCheck.length) { return undefined; }

    const newBase = Object.entries(updates).reduce((acc: any, [key, value]) => {
      const new_key: string = key;
      if (!updatesCheck.includes(new_key)) { return acc; }
      acc[new_key] = value;
      return acc;
    }, {});

    return newBase;
  };

  const onSubmit = async (formData: UpdateCustomerInfo) => {
    try {
      if (isSaving) return false;
      setIsSaving(true);

      if (formData.email && !isValidEmail(formData.email)) {
        toast({
          title: "Invalid email",
          description: "",
        });
        return false;
      }

      const parsedResult = !!isUpdateForm ? PartialUpdateCustomerValidation.safeParse(formData) : CreateCustomerValidation.safeParse(formData);
      if (!parsedResult.success) {
        toast({
          title: "Invalid Customer Data",
          description: parsedResult.error.issues[0].path as any,
        });
        return false;
      }

      const apiData = !!isUpdateForm ? deriveUpdateCustomerFields(parsedResult.data, originalUpdateCustomer) : parsedResult.data;
      if (!apiData) {
        if (!!isUpdateForm) {
          onUpdateCreateModalStatus(false);
          toast({
            title: "No changes",
            description: (parsedResult!.error as any).issues[0].path as any,
          });
        }

        return true;
      }

      const saveResult = await (!!isUpdateForm
        ? updateCustomer(updateCustomerId, apiData)
        : createCustomer(apiData));


      if (!saveResult.success) {
        switch (saveResult.message || "") {
          case "Invalid phone number":
            toast({
              title: "Invalid phone number",
              description: "",
            });
            break;
          case "Customer already exists":
            toast({
              title: "Customer already exists",
              description: "A customer with this ID already exists in the system",
            });
            break;
          default:
            toast({
              title: isUpdateForm
                ? "Failed to update customer"
                : "Failed to create customer",
              description: saveResult.message || "",
            });

        }

      } else {
        onUpdateCreateModalStatus(false);
        toast({
          title: isUpdateForm ? "Updated" : "Created",
          description: "",
        });
      }

      return true;
    } catch (error: any) {
      console.error('failed to manage customer', isUpdateForm, error)

      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      onOpenChange={onUpdateCreateModalStatus}
      open={isModalOpen}
      defaultOpen={false}
    >
      <DialogTrigger asChild>
        <Button
          type="button"
          className=""
          disabled={isModalOpen}
          onClick={() => onUpdateCreateModalStatus(true)}
        >
          Create Customer
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg md:max-w-2xl lg:max-w-5xl">
        <DialogHeader>
          <DialogTitle>
            {isUpdateForm ? "Update Customer" : "New Customer"}
          </DialogTitle>
        </DialogHeader>
        <CustomerForm onSubmit={onSubmit} />
        <DialogFooter className="sm:justify-start"></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CustomerForm({
  allowUpdatingClientId = false,
  onSubmit,
}: {
  allowUpdatingClientId?: boolean;
  onSubmit: (formData: UpdateCustomerInfo) => Promise<boolean>;
}) {
  const tradersResult = useListBaseTraders();
  const listOnboardingInstancesResult = useListOnboardingCustomerInstances();
  const [sourceEmailAddType, setSourceEmailAddType] = useState("payment_ach");
  const [sourcePhoneNumberAddType, setSourcePhoneNumberAddType] =
    useState("destination");

  const updateCustomerId = useBearStore((state) => state.updateCustomerId);
  const customer = useBearStore((state) => state.updateCustomer);
  const updateCustomerField = useBearStore(
    (state) => state.updateCustomerField,
  );

  const accountNumbersListsResult = useListClientBrokerAccountNumbers();
  const loadingOrErrorUi = GenerateLoadingOrError([accountNumbersListsResult, listOnboardingInstancesResult])
  const activeAccountCode = (customer.active_account_codes || [])[0];
  const {
    unassignedAccountNumberList,
    assignedAccountNumberList,
  } = accountNumbersListsResult?.data?.data || {};

  const onboardingInstances = listOnboardingInstancesResult?.data?.data || [];
  const onboardingInstanceOptions = onboardingInstances.map((instance: AvailableOnboardingInstanceInfo) => ({ value: instance.id, label: instance.trigger_meta_data.customer_name }));

  const { sortedAccountNumberOptions } = useMemo(() => {
    const activeOptions = (activeAccountCode &&
      !unassignedAccountNumberList?.find((accountCode: string) => {
        return accountCode === activeAccountCode;
      })
    ) ? [{ value: activeAccountCode, label: activeAccountCode }] : [];

    const sortedAccountNumberOptions = [
      ...activeOptions,
      ...(unassignedAccountNumberList?.map((accountNumber: string) => ({ value: accountNumber, label: accountNumber })) || [])
    ].sort();
    return { sortedAccountNumberOptions }
  }, [unassignedAccountNumberList || [], activeAccountCode || ""])

  if (loadingOrErrorUi) {
    return loadingOrErrorUi;
  }

  const isUpdateForm = !!updateCustomerId;
  const tradersList = tradersResult?.data?.data || [];
  const traderOptions = tradersList.map((trader: { id: string, name: string }) => ({ value: trader.id, label: trader.name }));

  return (
    <form
      onSubmit={(e) => {
        if (e?.preventDefault) e.preventDefault();
        return onSubmit(customer);
      }}
    >
      <div className="flex flex-col gap-4 md:grid md:grid-cols-2 lg:grid-cols-3">
        <div className="grid grid-cols-1 items-start gap-1 lg:col-span-1">
             {!isUpdateForm && (
               <div className="grid items-center gap-1.5">
                 <Label htmlFor="onboarding_instance_id">Onboarding Instance</Label>
                 <Combobox
                   isModal
                   dwimOnChange
                   className="w-full"
                   id="onboarding_instance_id"
                   value={customer.onboarding_instance_id || ""}
                   onValueChange={(value) => {
                     updateCustomerField("onboarding_instance_id", value);
                     
                     // Find the selected onboarding instance and prefill form fields
                     const selectedInstance = onboardingInstances.find(
                       (instance: AvailableOnboardingInstanceInfo) => instance.id === value
                     );
                     
                     if (selectedInstance?.trigger_meta_data) {
                       const metaData = selectedInstance.trigger_meta_data;
                       
                      updateCustomerField("long_name", metaData.customer_name);

                       // Prefill visible form fields
                       if (metaData.customer_address) {
                         updateCustomerField("address_line_one", metaData.customer_address);
                       } else {
                         updateCustomerField("address_line_one", metaData.customer_address_line_one || "");
                         updateCustomerField("address_line_two", metaData.customer_address_line_two || "");
                         updateCustomerField("city", metaData.customer_city || "");
                         updateCustomerField("state", metaData.customer_state || "");
                         updateCustomerField("zipcode", metaData.customer_zipcode || "");
                         updateCustomerField("country", metaData.customer_country || "");
                       }
                       if (metaData.trader_id) {
                         updateCustomerField("trader_id", metaData.trader_id);
                       }
                       if (metaData.email) {
                         updateCustomerField("email", metaData.email);
                       }
                       if (metaData.phone_number) {
                         updateCustomerField("phone_number", metaData.phone_number);
                       }
                       if (metaData.notes) {
                         updateCustomerField("notes", metaData.notes);
                       }
                       
                       // Store hidden fields for API submission
                       updateCustomerField("partner_names", metaData.partner_names || []);
                       updateCustomerField("shirt_sizes", metaData.shirt_sizes || []);
                       updateCustomerField("original_acres", metaData.number_of_acres);
                       updateCustomerField("bol_sequence", metaData.bol_sequence || "");
                     }
                   }}
                   options={onboardingInstanceOptions}
                 />
               </div>
             )}
          <div className="grid grid-cols-2 gap-2">
            <div className="grid items-center gap-1.5">
              <Label htmlFor="customerClientId">Id</Label>
              <Input
                required
                disabled={isUpdateForm && !allowUpdatingClientId}
                id="customerClientId"
                value={customer.client_id}
                onChange={(event) =>
                  updateCustomerField("client_id", event.target.value)
                }
              />
            </div>

            <div className="grid items-center gap-1.5">
              <Label htmlFor="short_name">Short Name</Label>
              <Input
                required
                id="short_name"
                value={customer.short_name}
                onChange={(event) =>
                  updateCustomerField("short_name", event.target.value)
                }
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-2">
            <div className="grid items-center gap-1.5">
              <Label htmlFor="long_name">Long Name</Label>
              <Input
                required
                id="long_name"
                value={customer.long_name}
                onChange={(event) =>
                  updateCustomerField("long_name", event.target.value)
                }
              />
            </div>
            <div className="grid items-center gap-1.5" >
              <Label htmlFor="trader_id">Trader</Label>
              <Combobox
                isModal
                dwimOnChange
                className=" w-full"
                id="trader_id"
                value={customer.trader_id || ""}
                onValueChange={(value) =>
                  updateCustomerField("trader_id", value as any)
                }
                options={traderOptions}
              />
            </div>

            <div className="grid items-center gap-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={customer.email}
                onChange={(event) =>
                  updateCustomerField("email", event.target.value)
                }
              />
            </div>
            <div className="grid items-center gap-1.5">
              <Label htmlFor="phone_number">Phone Number</Label>
              <Input
                id="phone_number"
                type="tel"
                value={
                  new PhoneNumberFormatterAsYouType("US").input(
                    customer.phone_number || "",
                  ) || ""
                }
                onChange={(event) =>
                  updateCustomerField("phone_number", event.target.value)
                }
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="grid items-center gap-1.5">
              <Label htmlFor="risk_sync">Risk Sync</Label>
              <Checkbox
                id="risk_sync"
                checked={!!customer.harvest_iq_profile}
                onCheckedChange={(value) => {
                  const newValue = value ? customer.long_name || "" : null;
                  updateCustomerField("harvest_iq_profile", newValue);
                }}
              />
            </div>
            <div className="grid items-center gap-1.5 hidden">
              <Label htmlFor="skip_harvest_iq_sync">Skip Risk Sync</Label>
              <Checkbox
                id="skip_harvest_iq_sync"
                checked={customer.skip_harvest_iq_sync}
                onCheckedChange={(value) =>
                  updateCustomerField("skip_harvest_iq_sync", value as any)
                }
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="grid items-center gap-1.5">
              <Label htmlFor="is_commercial">Commercial</Label>
              <Checkbox
                id="is_commercial"
                checked={customer.is_commercial}
                onCheckedChange={(value) =>
                  updateCustomerField("is_commercial", value as any)
                }
              />
            </div>
            <div className="grid items-center gap-1.5">
              <Label htmlFor="is_cfr">CFR</Label>
              <Checkbox
                id="is_cfr"
                checked={customer.is_cfr}
                onCheckedChange={(value) =>
                  updateCustomerField("is_cfr", value as any)
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-2 mt-1">
            <div className="grid items-center gap-1.5" >
              <Label htmlFor="preferred_defer_payment_type">Payment Type</Label>
              <Combobox
                isModal
                dwimOnChange
                className=" w-full"
                id="preferred_defer_payment_type"
                value={customer.preferred_defer_payment_type || ""}
                onValueChange={(value) =>
                  updateCustomerField("preferred_defer_payment_type", value as any)
                }
                options={DeferredPaymentTypeOptions}
              />
            </div>
            <div className="grid items-center gap-1.5" >
              <Label htmlFor="form_customer_type">Customer Type</Label>
              <Combobox
                isModal
                dwimOnChange
                className="w-full"
                id="form_customer_type"
                value={customer.customer_type || ""}
                onValueChange={(value) =>
                  updateCustomerField("customer_type", value as any)
                }
                options={customerTypeOptions}
              />
            </div>
            <div className="grid items-center gap-1.5">
              <Label htmlFor="rjo_account_number">RJO Account Number</Label>
              <Combobox
                isModal
                dwimOnChange
                className="w-full"
                id="rjo_account_number"
                value={activeAccountCode}
                onValueChange={(value) =>
                  updateCustomerField("active_account_codes", [value])
                }
                options={sortedAccountNumberOptions}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 items-start lg:col-span-2">
          <div className="flex flex-col gap-2 lg:grid lg:grid-cols-4 lg:col-span-4">
            <div className="flex flex-col lg:col-span-4 gap-2">
              <div className="grid items-center gap-1.5">
                <Label htmlFor="address_line_one">Address Line One</Label>
                <Input
                  id="address_line_one"
                  value={customer.address_line_one}
                  onChange={(event) =>
                    updateCustomerField("address_line_one", event.target.value)
                  }
                />
              </div>
              <div className="grid items-center gap-1.5">
                <Label htmlFor="address_line_two">Address Line Two</Label>
                <Input
                  id="address_line_two"
                  value={customer.address_line_two}
                  onChange={(event) =>
                    updateCustomerField(
                      "address_line_two",
                      event.target.value,
                    )
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 lg:col-span-4">
              <div className="grid items-center gap-1.5">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={customer.city}
                  onChange={(event) =>
                    updateCustomerField("city", event.target.value)
                  }
                />
              </div>
              <div className="grid items-center gap-1.5">
                <Label htmlFor="zipcode">Zipcode</Label>
                <Input
                  id="zipcode"
                  value={customer.zipcode}
                  onChange={(event) =>
                    updateCustomerField("zipcode", event.target.value)
                  }
                />
              </div>
              <div className="grid items-center gap-1.5">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={customer.state}
                  onChange={(event) =>
                    updateCustomerField(
                      "state",
                      event.target.value?.toLocaleUpperCase(),
                    )
                  }
                />
              </div>
              <div className="grid items-center gap-1.5">
                <Label htmlFor="country">Country</Label>
                <Combobox
                  isModal
                  dwimOnChange
                  className="w-full"
                  id="country"
                  value={customer.country || ""}
                  onValueChange={(value) =>
                    updateCustomerField("country", value)
                  }
                  options={[
                    { value: "USA", label: "USA" },
                    { value: "CANADA", label: "CANADA" }
                  ]}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 lg:col-span-4">
              <div className="grid items-center gap-1.5">
                <Label htmlFor="gps_latitude">GPS Latitude</Label>
                <Input
                  id="gps_latitude"
                  type="number"
                  step="any"
                  placeholder="e.g. 40.7128"
                  value={customer.gps_latitude || ""}
                  onChange={(event) =>
                    updateCustomerField("gps_latitude", event.target.value)
                  }
                />
              </div>
              <div className="grid items-center gap-1.5">
                <Label htmlFor="gps_longitude">GPS Longitude</Label>
                <Input
                  id="gps_longitude"
                  type="number"
                  step="any"
                  placeholder="e.g. -74.0060"
                  value={customer.gps_longitude || ""}
                  onChange={(event) =>
                    updateCustomerField("gps_longitude", event.target.value)
                  }
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 items-start gap-1.5 w-full">
            <div className="flex flex-row gap-2 justify-between items-center">
              <Label>Other Numbers</Label>
              <span>
                <Combobox
                  isModal
                  dwimOnChange
                  options={["destination", "mobile_user"].map((value) => ({
                    label: generatePhoneNumberSourceTypeLabel(value),
                    value,
                  }))}
                  value={sourcePhoneNumberAddType}
                  onValueChange={(value: string) => {
                    setSourcePhoneNumberAddType(value);
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="ml-2"
                  onClick={() => {
                    const newPhoneNumber = {
                      source_type: sourcePhoneNumberAddType,
                      label: "",
                      phone_number: "",
                    };
                    updateCustomerField("source_phone_numbers", [
                      newPhoneNumber,
                      ...(customer.source_phone_numbers || []),
                    ]);
                  }}
                >
                  +
                </Button>
              </span>
            </div>

            {(customer.source_phone_numbers || []).map((phone, index) => (
              <div
                key={index}
                className="flex flex-row gap-2 mt-2 items-center sm:grid sm:grid-cols-[minmax(auto,100px)_1fr_1fr_auto] sm:gap-2"
              >
                <span className="text-sm text-gray-500">
                  {generatePhoneNumberSourceTypeLabel(phone.source_type)}
                </span>
                <Input
                  placeholder="Phone Label"
                  value={phone.label}
                  onChange={(event) => {
                    const updated = [...(customer.source_phone_numbers || [])];
                    updated[index] = {
                      ...phone,
                      label: event.target.value || "",
                    };
                    updateCustomerField("source_phone_numbers", updated);
                  }}
                />
                <Input
                  type="tel"
                  placeholder="Phone Number"
                  value={
                    new PhoneNumberFormatterAsYouType("US").input(
                      phone.phone_number || "",
                    ) || ""
                  }
                  onChange={(event) => {
                    const updated = [...(customer.source_phone_numbers || [])];
                    updated[index] = {
                      ...phone,
                      phone_number: event.target.value || "",
                    };
                    updateCustomerField("source_phone_numbers", updated);
                  }}
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    const updated = customer.source_phone_numbers?.filter(
                      (_, i) => i !== index,
                    );
                    updateCustomerField("source_phone_numbers", updated);
                  }}
                >
                  X
                </Button>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 items-start gap-1.5 mt-4 w-full">
            <div className="flex flex-row gap-2 justify-between items-center">
              <Label>Other Emails</Label>
              <span>
                <Combobox
                  isModal
                  dwimOnChange
                  options={["payment_ach", "payment_check", "pdf_signature"].map((value) => ({
                    label: generateEmailSourceTypeLabel(value),
                    value,
                  }))}
                  value={sourceEmailAddType}
                  onValueChange={(value: string) => {
                    setSourceEmailAddType(value);
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="ml-2"
                  onClick={() => {
                    const newEmail = {
                      source_type: sourceEmailAddType,
                      label: "",
                      email: "",
                    };
                    updateCustomerField("source_emails", [
                      newEmail,
                      ...(customer.source_emails || []),
                    ]);
                  }}
                >
                  +
                </Button>
              </span>
            </div>

            {(customer.source_emails || []).map((email, index) => (
              <div
                key={index}
                className="flex flex-row gap-2 mt-2 items-center  sm:grid sm:grid-cols-[minmax(auto,100px)_1fr_1fr_auto] sm:gap-2"
              >
                <span className="text-sm text-gray-500">
                  {generateEmailSourceTypeLabel(email.source_type)}
                </span>
                <Input
                  placeholder="Email Label"
                  value={email.label}
                  onChange={(event) => {
                    const updated = [...(customer.source_emails || [])];
                    updated[index] = {
                      ...email,
                      label: event.target.value || "",
                    };
                    updateCustomerField("source_emails", updated);
                  }}
                />
                <Input
                  type="email"
                  placeholder="Email"
                  value={email.email}
                  onChange={(event) => {
                    const updated = [...(customer.source_emails || [])];
                    updated[index] = {
                      ...email,
                      email: event.target.value || "",
                    };
                    updateCustomerField("source_emails", updated);
                  }}
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    const updated = customer.source_emails?.filter(
                      (_, i) => i !== index,
                    );
                    updateCustomerField("source_emails", updated);
                  }}
                >
                  X
                </Button>
              </div>
            ))}
          </div>
            <div className="grid items-center gap-1.5 w-full">
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                value={customer.notes || ""}
                onChange={(event) =>
                  updateCustomerField("notes", event.target.value)
                }
              />
            </div>
            <div className="grid items-center gap-1.5 w-full">
              <Label htmlFor="signed_up_date">Signup Date</Label>
              <Input
                id="signed_up_date"
                type="date"
                value={customer.signed_up_date || ""}
                onChange={(event) =>
                  updateCustomerField("signed_up_date", event.target.value)
                }
              />
            </div>
        </div>

        <div className="flex flex-row justify-between gap-2 md:col-span-2 lg:col-span-3">
          <DialogClose asChild>
            <Button type="button" variant="secondary">
              Exit
            </Button>
          </DialogClose>

          <Button type="submit" size="sm" className="px-3">
            {isUpdateForm ? "Update" : "Create"}
          </Button>
        </div>
      </div>
    </form>
  );
}

function generateEmailSourceTypeLabel(source_type: string) {
  switch (source_type) {
    case "payment":
      return "Payment";
    case "payment_ach":
      return "Payment ACH";
    case "payment_check":
      return "Payment Check";
    case "pdf_signature":
      return "PDF Signature";
    default:
      return "Unknown";
  }
}

function generatePhoneNumberSourceTypeLabel(source_type: string) {
  switch (source_type) {
    case "destination":
      return "Destination";
    case "mobile_user":
      return "Mobile";
    default:
      return "Unknown";
  }
}

function transformToCustomerComparablePosition(original: any) {
  return {
    short_name: original.short_name === null ? null : original.short_name,
    long_name: original.long_name === null ? null : original.long_name,

    harvest_iq_profile: original.harvest_iq_profile === null ? null : original.harvest_iq_profile,
    skip_harvest_iq_sync: original.skip_harvest_iq_sync === null ? false : original.skip_harvest_iq_sync,
    is_cfr: original.is_cfr === null ? false : original.is_cfr,
    is_commercial: original.is_commercial === null ? false : original.is_commercial,

    email: original.email === null ? null : original.email,
    phone_number: original.phone_number === null ? null : original.phone_number,

    source_phone_numbers: original.source_phone_numbers ? original.source_phone_numbers : [],
    source_emails: original.source_emails ? original.source_emails : [],

    address_line_one: original.address_line_one === null ? null : original.address_line_one,
    address_line_two: original.address_line_two === null ? null : original.address_line_two,
    city: original.city === null ? null : original.city,
    state: original.state === null ? null : original.state,
    zipcode: original.zipcode === null ? null : original.zipcode,
    country: original.country === null ? null : original.country,

    gps_latitude: original.gps_latitude === null ? null : original.gps_latitude,
    gps_longitude: original.gps_longitude === null ? null : original.gps_longitude,

    preferred_defer_payment_type: original.preferred_defer_payment_type === null ? null : original.preferred_defer_payment_type,
    deferred_payment_lien_status: original.deferred_payment_lien_status === null ? null : original.deferred_payment_lien_status,

    customer_type: original.customer_type,
    active_account_codes: original.active_account_codes === null ? null : original.active_account_codes,
    notes: !original.notes ? null : original.notes,

    trader_id: original.trader_id === null ? null : original.trader_id,
    signed_up_date: original.signed_up_date === null ? null : original.signed_up_date,
  };
}

export default ManageCustomersPage;
